import React from 'react'
import { Document, Page, View, Text, Image } from '@react-pdf/renderer'
import {
    Lesson,
    Question,
    MultipleChoiceQuestion,
    FillBlankQuestion,
    ShortAnswerQuestion,
} from '@all-for-one/shared-types'
import { S, PdfFooter, PdfBaseProps } from './pdf-shared'
import { LEVEL_LETTERS, WS_SECTION_ORDER, getQuestionsForLevelSection, getWsQuestionTypes } from './pdf-content-selection'

/**
 * The student worksheet document -- full interactive-question layout
 * (options/blanks/ruled-lines), per level+section. See
 * docs/BLUEPRINT-2026-09-30-pdf-documents-decomposition.md.
 */

export interface WorksheetEntry {
    level: string
    sections: string[]
}

// ── Question rendering ────────────────────────────────────────────────────────

function renderMCQuestion(q: MultipleChoiceQuestion, label: string, isWorksheet: boolean, imageMap: Record<string, string>) {
    const letters = ['A', 'B', 'C', 'D']
    const img = (url: string) => imageMap[url] ?? url
    const useVertical = q.options.some(opt => opt.length > 25)
    return (
        <View style={isWorksheet ? S.questionWs : S.question} wrap={false}>
            {isWorksheet && q.imageUrl ? <Image src={img(q.imageUrl)} style={S.questionImage} /> : null}
            <View style={S.questionRow}>
                {label ? <Text style={S.questionLabel}>{label} </Text> : null}
                <Text style={S.questionText}>{q.question}</Text>
            </View>
            <View style={useVertical ? S.mcOptionsCol : S.mcOptionsRow}>
                {q.options.map((opt, i) => (
                    <View key={i} style={useVertical ? S.mcOptionFull : S.mcOptionFlex}>
                        {isWorksheet && q.optionImageUrls?.[i]
                            ? <Image src={img(q.optionImageUrls[i])} style={S.mcOptionImage} />
                            : null}
                        <View style={S.mcOptionRow}>
                            <Text style={S.mcLetter}>{letters[i]}.</Text>
                            <Text style={{ flexShrink: 1 }}>{opt}</Text>
                        </View>
                    </View>
                ))}
            </View>
        </View>
    )
}

function renderFillBlankQuestion(q: FillBlankQuestion, label: string, isWorksheet: boolean, imageMap: Record<string, string>) {
    const img = (url: string) => imageMap[url] ?? url
    return (
        <View style={isWorksheet ? S.questionWs : S.question} wrap={false}>
            {isWorksheet && q.imageUrl ? <Image src={img(q.imageUrl)} style={S.questionImage} /> : null}
            <View style={S.fillBlankRow}>
                {label ? <Text style={S.questionLabel}>{label} </Text> : null}
                {q.question.map((part, i) => (
                    <React.Fragment key={i}>
                        <Text style={S.questionText}>{part}</Text>
                        {i < q.question.length - 1 ? <View style={S.blankLine} /> : null}
                    </React.Fragment>
                ))}
            </View>
        </View>
    )
}

function renderShortAnswerQuestion(q: ShortAnswerQuestion, label: string, isWorksheet: boolean, imageMap: Record<string, string>) {
    const img = (url: string) => imageMap[url] ?? url
    return (
        <View style={isWorksheet ? S.questionWs : S.question} wrap={false}>
            {isWorksheet && q.imageUrl ? <Image src={img(q.imageUrl)} style={S.questionImage} /> : null}
            <View style={S.questionRow}>
                {label ? <Text style={S.questionLabel}>{label} </Text> : null}
                <Text style={S.questionText}>{q.question}</Text>
            </View>
            <View style={S.shortAnswerLines}>
                {[0, 1, 2, 3, 4].map(i => <View key={i} style={S.ruledLine} />)}
            </View>
        </View>
    )
}

function renderQuestion(q: Question, label: string, isWorksheet: boolean, imageMap: Record<string, string>): React.ReactNode {
    switch (q.type) {
        case 'multiple-choice':
            return renderMCQuestion(q as MultipleChoiceQuestion, label, isWorksheet, imageMap)
        case 'fill-blank':
            return renderFillBlankQuestion(q as FillBlankQuestion, label, isWorksheet, imageMap)
        case 'short-answer':
            return renderShortAnswerQuestion(q as ShortAnswerQuestion, label, isWorksheet, imageMap)
        default:
            return null
    }
}

// ── Worksheet body ─────────────────────────────────────────────────────────────

const WorksheetContent = ({
    lesson, level, sections, assignedClassName, gradeBand, subject, logoDataUrl, imageMap,
}: { lesson: Lesson; level: string; sections: string[]; assignedClassName?: string; gradeBand?: string; subject?: string; logoDataUrl: string; imageMap: Record<string, string> }) => {
    const types = getWsQuestionTypes(lesson, level, sections)
    const dirLines: string[] = []
    if (types.has('multiple-choice')) dirLines.push('Circle the letter of the correct answer.')
    if (types.has('fill-blank')) dirLines.push('Write the missing word or words on the line.')
    if (types.has('short-answer')) dirLines.push('Write your answer in the lines provided.')

    return (
        <View>
            {/* Name line at top */}
            <View style={S.wsTopRow}>
                <View style={S.wsNameRow}>
                    <Text style={S.wsNameLabel}>Name:</Text>
                    <View style={S.wsNameBlank} />
                </View>
            </View>

            {/* Logo + lesson info header */}
            <View style={S.wsDocHeader}>
                <View style={S.headerLeft}>
                    {logoDataUrl ? <Image src={logoDataUrl} style={S.logo} /> : null}
                    <View>
                        <Text style={S.lessonName}>{lesson.name}</Text>
                        {assignedClassName ? <Text style={S.headerDetail}>Class: {assignedClassName}</Text> : null}
                        {subject ? <Text style={S.headerDetail}>Subject: {subject}</Text> : null}
                        {gradeBand ? <Text style={S.headerDetail}>Grade band: {gradeBand}</Text> : null}
                    </View>
                </View>
            </View>

            {/* Directions */}
            {dirLines.length > 0 && (
                <View style={S.directions}>
                    {dirLines.map((l, i) => <Text key={i} style={S.directionLine}>{l}</Text>)}
                </View>
            )}

            {/* Sections */}
            {WS_SECTION_ORDER.filter(s => sections.includes(s)).map(section => {
                const groups = getQuestionsForLevelSection(lesson, level, section)
                if (groups.length === 0) return null
                const isCfu = section === 'Check for Understanding'
                let pos = 0
                return (
                    <View key={section}>
                        <Text style={S.sectionHeader}>{section}</Text>
                        {groups.map(group =>
                            group.questions.map(q => {
                                pos++
                                const label = isCfu ? '' : `${pos}.`
                                return (
                                    <View key={q.id}>
                                        {isCfu ? <Text style={S.slideNote}>Answer after slide {group.slide + 1}</Text> : null}
                                        {renderQuestion(q, label, true, imageMap)}
                                    </View>
                                )
                            })
                        )}
                    </View>
                )
            })}
        </View>
    )
}

export interface WorksheetPdfProps extends PdfBaseProps {
    worksheets: WorksheetEntry[]
    level: string
    imageMap: Record<string, string>
}

export const WorksheetPdfDocument = ({
    lesson, assignedClassName, gradeBand, subject, logoDataUrl, downloadDate, worksheets, level, imageMap,
}: WorksheetPdfProps) => (
    <Document>
        <Page style={S.page} size="LETTER">
            {worksheets.map((ws, i) => (
                <View key={i} break={i > 0}>
                    <WorksheetContent
                        lesson={lesson}
                        level={ws.level}
                        sections={ws.sections}
                        assignedClassName={assignedClassName}
                        gradeBand={gradeBand}
                        subject={subject}
                        logoDataUrl={logoDataUrl}
                        imageMap={imageMap}
                    />
                </View>
            ))}
            <PdfFooter lessonName={lesson.name} downloadDate={downloadDate} levelLetter={LEVEL_LETTERS[level]} />
        </Page>
    </Document>
)
